/**
 * Centralized error tracking via Sentry.
 *
 * Use reportError() instead of console.error() in services and store slices
 * so errors are both logged locally and sent to Sentry with context.
 */

type SentryModule = typeof import('@sentry/react');
type SentryInitOptions = Parameters<SentryModule['init']>[0];

let sentryModulePromise: Promise<SentryModule> | null = null;

function loadSentry(): Promise<SentryModule> {
  sentryModulePromise ??= import('@sentry/react');
  return sentryModulePromise;
}

export async function initErrorTracking(options: SentryInitOptions): Promise<void> {
  const Sentry = await loadSentry();
  Sentry.init(options);
}

/**
 * Report an error to Sentry with optional context.
 * Also logs to console.error for local debugging.
 */
export function reportError(
  error: unknown,
  context?: {
    /** Where the error happened (e.g., 'addCard', 'translateWord') */
    operation?: string;
    /** Extra data to attach to the Sentry event */
    extra?: Record<string, unknown>;
  },
): void {
  const message = error instanceof Error ? error.message : String(error);
  console.error(`[${context?.operation ?? 'unknown'}]`, message);

  void loadSentry().then((Sentry) => {
    Sentry.captureException(error, {
      tags: { operation: context?.operation },
      extra: context?.extra,
    });
  });
}

/**
 * Add a breadcrumb for user actions (shown in Sentry error timeline).
 */
export function trackBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  void loadSentry().then((Sentry) => {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: 'info',
    });
  });
}
